
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import Index from "@/pages/Index";
import Profile from "@/pages/Profile";
import Auth from "@/pages/Auth";
import Welcome from "@/pages/Welcome";
import CreatePost from "@/pages/CreatePost";
import Groups from "@/pages/Groups";
import GroupDetail from "@/pages/GroupDetail";
import Notifications from "@/pages/Notifications";
import AdminDashboard from "@/pages/AdminDashboard";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import { HashtagPage } from "@/pages/HashtagPage";
import Friends from "@/pages/Friends";
import { SubscriptionGuard } from "@/components/guards/SubscriptionGuard";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

// Auth wrapper component
const AuthWrapper = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  useEffect(() => {
    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN") {
        // Don't redirect from auth pages
        if (!location.pathname.includes('/auth')) {
          toast({
            title: "Welcome back! 👋",
            description: "You've successfully signed in.",
          });
        } else {
          // Redirect to home page if on auth page
          navigate("/");
        }
      } else if (event === "SIGNED_OUT") {
        navigate("/auth");
        toast({
          title: "Signed out",
          description: "You've been successfully signed out.",
        });
      } else if (event === "USER_UPDATED") {
        toast({
          title: "Profile updated",
          description: "Your profile information has been updated.",
        });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, location.pathname, toast]);

  return <>{children}</>;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthWrapper>
          <Routes>
            <Route path="/" element={<SubscriptionGuard><Index /></SubscriptionGuard>} />
            <Route path="/profile" element={<SubscriptionGuard><Profile /></SubscriptionGuard>} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/create" element={<SubscriptionGuard><CreatePost /></SubscriptionGuard>} />
            <Route path="/groups" element={<SubscriptionGuard><Groups /></SubscriptionGuard>} />
            <Route path="/groups/:groupId" element={<SubscriptionGuard><GroupDetail /></SubscriptionGuard>} />
            <Route path="/friends" element={<SubscriptionGuard><Friends /></SubscriptionGuard>} />
            <Route path="/notifications" element={<SubscriptionGuard><Notifications /></SubscriptionGuard>} />
            <Route path="/admin" element={<SubscriptionGuard><AdminDashboard /></SubscriptionGuard>} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/hashtags/:tag" element={<SubscriptionGuard><HashtagPage /></SubscriptionGuard>} />
            {/* Add a redirect for /home to the root path */}
            <Route path="/home" element={<SubscriptionGuard><Index /></SubscriptionGuard>} />
          </Routes>
        </AuthWrapper>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
